import React from 'react';
import { clearCorruptedStorage, RELOAD_COUNT_KEY } from 'utils/storage';

const MAX_RELOADS = 2;

interface State {
  crashed: boolean;
  clearing: boolean;
}

// Heuristic — if the error message/stack smells like a storage parse problem,
// auto-clear and reload instead of showing the error UI.
const STORAGE_PATTERNS = [
  'localstorage',
  'sessionstorage',
  'storage',
  'quota',
  'quotaexceedederror',
  'syntaxerror',
  'unexpected token',
  'unexpected end of json',
];

function looksLikeStorageError(error: Error): boolean {
  const haystack = `${error.name} ${error.message} ${error.stack ?? ''}`.toLowerCase();
  return STORAGE_PATTERNS.some(p => haystack.includes(p));
}

export default class StorageErrorBoundary extends React.Component<{ children: React.ReactNode }, State> {
  state: State = { crashed: false, clearing: false };

  static getDerivedStateFromError(): Partial<State> {
    return { crashed: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[BAO Storage] Render crash caught by boundary:', error, info);

    if (looksLikeStorageError(error)) {
      this.clearAndReload();
    }
  }

  clearAndReload() {
    const count = parseInt(sessionStorage.getItem(RELOAD_COUNT_KEY) ?? '0', 10);
    if (count >= MAX_RELOADS) {
      // Too many auto-reloads — stop and show the static error UI.
      this.setState({ clearing: false });
      return;
    }
    sessionStorage.setItem(RELOAD_COUNT_KEY, String(count + 1));
    this.setState({ clearing: true });
    clearCorruptedStorage();
    // Small delay so the "clearing" message renders before the reload.
    setTimeout(() => window.location.reload(), 500);
  }

  render() {
    if (!this.state.crashed) return this.props.children;

    if (this.state.clearing) {
      return (
        <div style={styles.center}>
          <p style={{ fontFamily: 'sans-serif', color: '#555' }}>Nettoyage du cache en cours…</p>
        </div>
      );
    }

    return (
      <div style={styles.center}>
        <div style={styles.box}>
          <h2 style={{ marginTop: 0, fontFamily: 'sans-serif', color: '#1a1a1a' }}>Oups, une erreur est survenue</h2>
          <p style={{ fontFamily: 'sans-serif', color: '#555', lineHeight: 1.5 }}>
            L&apos;application a planté. Vider le cache règle généralement ce problème.
          </p>
          <button style={styles.button} onClick={() => this.clearAndReload()}>
            Vider le cache et recharger
          </button>
          <button
            style={{ ...styles.button, background: '#6b7280', marginLeft: 10 }}
            onClick={() => this.setState({ crashed: false, clearing: false })}
          >
            Réessayer sans vider
          </button>
        </div>
      </div>
    );
  }
}

const styles = {
  center: {
    display: 'flex' as const,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    height: '100vh',
  },
  box: {
    textAlign: 'center' as const,
    maxWidth: 440,
    padding: '32px 24px',
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
  },
  button: {
    padding: '10px 20px',
    cursor: 'pointer',
    background: '#2563eb',
    color: 'white',
    border: 'none',
    borderRadius: 8,
    fontSize: 14,
    fontFamily: 'sans-serif',
    marginTop: 16,
  },
};
